import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Save, Building2, CreditCard, Phone, MapPin, Lock, Eye, EyeOff, Upload, Trash2, FileSignature } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    cnp: '',
    ci_series: '',
    ci_number: '',
    company_name: 'S.C. ADSEM MARKETING S.R.L.',
    position: '',
    address: '',
    phone: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Signature state
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const signatureInputRef = useRef(null);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    loadProfile();
    loadUserSignature();
  }, []);

  const loadUserSignature = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data.signature_url) {
        setSignatureUrl(response.data.signature_url);
      }
    } catch (err) {
      console.error('Failed to load signature');
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Te rog selectează o imagine (PNG, JPG)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imaginea nu poate depăși 5MB');
      return;
    }
    
    setUploadingSignature(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/users/${user.id}/signature`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSignatureUrl(response.data.signature_url);
      toast.success('Semnătura a fost încărcată!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la încărcarea semnăturii');
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleDeleteSignature = async () => {
    if (!confirm('Sigur vrei să ștergi semnătura?')) return;
    
    try {
      await api.delete(`/users/${user.id}/signature`);
      setSignatureUrl(null);
      toast.success('Semnătura a fost ștearsă');
    } catch (err) {
      toast.error('Eroare la ștergerea semnăturii');
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/users/${user.id}/employee-profile`);
      setProfile({
        full_name: res.data.full_name || '',
        email: res.data.email || '',
        cnp: res.data.cnp || '',
        ci_series: res.data.ci_series || '',
        ci_number: res.data.ci_number || '',
        company_name: res.data.company_name || 'S.C. ADSEM MARKETING S.R.L.',
        position: res.data.position || '',
        address: res.data.address || '',
        phone: res.data.phone || ''
      });
    } catch (err) {
      toast.error('Eroare la încărcarea profilului');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put(`/users/${user.id}/employee-profile`, {
        cnp: profile.cnp,
        ci_series: profile.ci_series,
        ci_number: profile.ci_number,
        company_name: profile.company_name,
        position: profile.position,
        address: profile.address,
        phone: profile.phone
      });
      toast.success('Profilul a fost actualizat!');
    } catch (err) {
      toast.error('Eroare la salvarea profilului');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Parolele noi nu coincid');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Parola nouă trebuie să aibă minim 6 caractere');
      return;
    }
    
    setChangingPassword(true);
    try {
      console.log('Attempting password change...');
      const response = await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      console.log('Password change response:', response.data);
      toast.success('Parola a fost schimbată cu succes!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      console.error('Password change error:', err.response?.status, err.response?.data);
      const errorMessage = err.response?.data?.detail || 
        (err.response?.status === 401 ? 'Sesiunea a expirat. Te rugăm să te reautentifici.' : 
        'Eroare la schimbarea parolei');
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <User className="w-7 h-7 text-blue-500" />
            Profilul Meu
          </h1>
          <p className="text-muted-foreground">
            Actualizează datele tale de identificare pentru cererile de concediu
          </p>
        </div>

        <form onSubmit={handleSave}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                Informații de Bază
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nume Complet</Label>
                  <Input
                    value={profile.full_name}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={profile.email}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position">Funcție / Post *</Label>
                  <Input
                    id="position"
                    value={profile.position}
                    onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                    placeholder="ex: Specialist Marketing Digital"
                    data-testid="profile-position"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="07xx xxx xxx"
                      className="pl-10"
                      data-testid="profile-phone"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Date Companie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Firma / Angajator *</Label>
                <Input
                  id="company_name"
                  value={profile.company_name}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="ex: S.C. ADSEM MARKETING S.R.L."
                  data-testid="profile-company"
                />
              </div>
            </CardContent>
          </Card>

          {/* Identity Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Date de Identificare
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cnp">CNP (Cod Numeric Personal) *</Label>
                <Input
                  id="cnp"
                  value={profile.cnp}
                  onChange={(e) => setProfile({ ...profile, cnp: e.target.value })}
                  placeholder="ex: 2900101123456"
                  maxLength={13}
                  data-testid="profile-cnp"
                />
                <p className="text-xs text-muted-foreground">13 cifre - necesar pentru cererile oficiale</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ci_series">Seria CI *</Label>
                  <Input
                    id="ci_series"
                    value={profile.ci_series}
                    onChange={(e) => setProfile({ ...profile, ci_series: e.target.value.toUpperCase() })}
                    placeholder="ex: XZ"
                    maxLength={2}
                    className="uppercase"
                    data-testid="profile-ci-series"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ci_number">Număr CI *</Label>
                  <Input
                    id="ci_number"
                    value={profile.ci_number}
                    onChange={(e) => setProfile({ ...profile, ci_number: e.target.value })}
                    placeholder="ex: 1234567"
                    data-testid="profile-ci-number"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Adresă Domiciliu</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="address"
                    value={profile.address}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    placeholder="ex: Str. Exemplu nr. 1, București"
                    className="pl-10"
                    data-testid="profile-address"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700">
                <strong>Important:</strong> Aceste date sunt necesare pentru generarea cererilor oficiale de concediu. 
                Asigură-te că informațiile sunt corecte și actualizate.
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            type="submit" 
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Se salvează...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Salvează Profilul
              </span>
            )}
          </Button>
        </form>

        {/* Password Change Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Schimbare Parolă
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
              <div className="space-y-2">
                <Label htmlFor="current_password">Parola Curentă</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Parola curentă"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">Parola Nouă</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Minim 6 caractere"
                    required
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmă Parola Nouă</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Repetă parola nouă"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                variant="outline"
                className="w-full"
                disabled={changingPassword}
                data-testid="change-password-btn"
              >
                {changingPassword ? 'Se procesează...' : 'Schimbă Parola'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              Semnătură Electronică
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Încarcă semnătura ta pentru a fi aplicată automat pe documentele oficiale (cereri de concediu, rapoarte, etc.)
            </p>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              {signatureUrl ? (
                <div className="space-y-4">
                  <div className="bg-white border rounded-lg p-4 inline-block">
                    <img 
                      src={`${BACKEND_URL}${signatureUrl}`} 
                      alt="Semnătura ta"
                      className="max-h-24 max-w-full object-contain"
                    />
                  </div>
                  <div className="flex justify-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => signatureInputRef.current?.click()}
                      disabled={uploadingSignature}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Schimbă
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleDeleteSignature}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Șterge
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <FileSignature className="w-12 h-12 mx-auto text-slate-300" />
                  <p className="text-sm text-slate-500">Nu ai încă o semnătură setată</p>
                  <Button 
                    onClick={() => signatureInputRef.current?.click()}
                    disabled={uploadingSignature}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingSignature ? 'Se încarcă...' : 'Încarcă Semnătura'}
                  </Button>
                </div>
              )}
              <input
                ref={signatureInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleSignatureUpload}
              />
            </div>
            
            <p className="text-xs text-muted-foreground mt-3">
              Formate acceptate: PNG, JPG (recomandat PNG cu fundal transparent). Max 5MB.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Profile;
